import { getMouseflowDetails } from '@app/api/deal-room.api';
import { Modal } from '@app/components/common/Modal/Modal';
import { Spinner } from '@app/components/common/Spinner/Spinner';
import { LevelType } from '@app/constants/enums/mouseflow-level';
import { IMouseflowScore } from '@app/interfaces/mouseflow-score';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as S from './MouseflowDetailsModal.styles';

interface MouseflowDetailsModalProps {
  open: boolean;
  dealRoomId: string;
  onClose: () => void;
}

const formatDuration = (ms: number): string => {
  if (!ms || ms <= 0) return '0s';
  const totalSeconds = Math.floor(ms / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  const parts: string[] = [];
  if (hours) parts.push(`${hours}h`);
  if (minutes) parts.push(`${minutes}m`);
  if (seconds || !parts.length) parts.push(`${seconds}s`);
  return parts.join(' ');
};

export const MouseflowDetailsModal: React.FC<MouseflowDetailsModalProps> = ({ open, dealRoomId, onClose }) => {
  const { t } = useTranslation();
  const [data, setData] = useState<IMouseflowScore | null>(null);
  const [loading, setLoading] = useState(false);

  const getTranslatedSections = (sections: string[]): string[] => {
    const sectionTranslations: Record<string, string> = {
      'decided-to-purchase-the-insurance': t('dealRooms.sections.decidedToPurchaseTheInsurance'),
      apply: t('dealRooms.mouseflow.sections.apply'),
      pricing: t('dealRooms.mouseflow.sections.pricing'),
      'insurance-coverage-shared-building': t('dealRooms.sections.insuranceCoverageSharedBuilding'),
      'shared-building-structure': t('dealRooms.sections.sharedBuildingStructure'),
      'shared-building-contents': t('dealRooms.sections.sharedBuildingContents'),
      'third-party-insurance': t('dealRooms.sections.thirdPartyInsurance'),
      'employers-liability': t('dealRooms.sections.employersLiability'),
      'personal-claims-for-committee-members': t('dealRooms.sections.personalClaimsForCommitteeMembers'),
      earthquake: t('dealRooms.sections.earthquake'),
      'electric-charging-station': t('dealRooms.landing.headerSections.electricChargingStation'),
      claims: t('dealRooms.mouseflow.sections.claims'),
      testimonials: t('dealRooms.mouseflow.sections.testimonials'),
      'listen-to-our-podcasts': t('dealRooms.sections.listenToOurPodcasts'),
      'podcast-insurance': t('dealRooms.sections.podcastInsurance'),
      'podcast-legal': t('dealRooms.sections.podcastLegal'),
      'podcast-management-app': t('dealRooms.sections.podcastManagementApp'),
      'podcast-fire-safety': t('dealRooms.sections.podcastFireSafety'),
      'podcast-management-companies': t('dealRooms.sections.podcastManagementCompanies'),
      'podcast-maintenance': t('dealRooms.sections.podcastMaintenance'),
      'podcast-new-buildings-form-4': t('dealRooms.sections.podcastNewBuildingsForm4'),
      documents: t('dealRooms.sections.documents'),
      attachments: t('dealRooms.sections.attachments'),
      coverage: t('dealRooms.mouseflow.sections.coverage'),
      product: t('dealRooms.mouseflow.sections.product'),
      about: t('dealRooms.mouseflow.sections.about'),
      chat: t('dealRooms.mouseflow.sections.chat'),
      opening: t('dealRooms.sections.opening'),
    };
    return sections.map((section) => sectionTranslations[section] || section);
  };

  useEffect(() => {
    if (!open || !dealRoomId) return;
    let cancelled = false;
    setLoading(true);
    getMouseflowDetails(dealRoomId)
      .then((res) => {
        if (!cancelled) setData(res);
      })
      .catch(() => {
        if (!cancelled) setData(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, dealRoomId]);

  const renderBody = () => {
    if (loading) {
      return (
        <S.LoadingWrapper>
          <Spinner spinning />
        </S.LoadingWrapper>
      );
    }
    if (!data) {
      return <S.EmptyState>{t('dealRooms.mouseflow.noData')}</S.EmptyState>;
    }
    const level = data.level as LevelType;
    return (
      <S.Table>
        <tbody>
          <tr>
            <th>{t('dealRooms.mouseflow.score')}</th>
            <td>{data.score ?? 0}</td>
          </tr>
          <tr>
            <th>{t('dealRooms.mouseflow.level')}</th>
            <td>
              <S.LevelBadge $level={level}>{t(`dealRooms.mouseflow.levels.${level}`)}</S.LevelBadge>
            </td>
          </tr>
          <tr>
            <th>{t('dealRooms.mouseflow.totalSessions')}</th>
            <td>{data.totalSessions ?? 0}</td>
          </tr>
          <tr>
            <th>{t('dealRooms.mouseflow.totalDurationMs')}</th>
            <td>{formatDuration(data.totalDurationMs)}</td>
          </tr>
          <tr>
            <th>{t('dealRooms.mouseflow.totalSectionsSeen')}</th>
            <td dangerouslySetInnerHTML={{ __html: getTranslatedSections(data.totalSectionsSeen).join('<br>') }} />
          </tr>
          <tr>
            <th>{t('dealRooms.mouseflow.totalEngagementTimeMs')}</th>
            <td>{formatDuration(data.totalEngagementTimeMs)}</td>
          </tr>
        </tbody>
      </S.Table>
    );
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title={t('dealRooms.mouseflow.title')}
      footer={null}
      size="medium"
      destroyOnClose
    >
      {renderBody()}
    </Modal>
  );
};
